import React, { useState, useEffect } from 'react';
import { X, Clock, Mail, Filter, Save, Database, Building2, Globe, User, Info } from 'lucide-react';
import {
  createReportSubscription,
  updateReportSubscription,
  validateReportSubscription,
  createNativeReportSubscription,
  updateNativeReportSubscription,
  validateNativeReportSubscription,
  getReports,
  getOrgConfig,
  getUsersInOrganization,
  getUserAccessibleLocations
} from '../../utils/api';
import { getLocationsByOrgId } from '../../utils/locationApi';
import { useUserSettingsContext } from '../../context/UserSettingsContext';

const REPORT_TYPE_POWERBI = 'powerbi';
const REPORT_TYPE_NATIVE = 'native';

const UnifiedSubscriptionForm = ({ subscription, currentOrganization, onSave, onCancel, mode = 'create' }) => {
  const { userSettings } = useUserSettingsContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [availableReports, setAvailableReports] = useState([]);
  const [availableLocations, setAvailableLocations] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState(REPORT_TYPE_POWERBI);

  // Delegated subscription creation (creating for multiple users)
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedTargetUsers, setSelectedTargetUsers] = useState([userSettings?.email || '']);
  const [targetUsersInfo, setTargetUsersInfo] = useState({});
  const [loadingUserLocations, setLoadingUserLocations] = useState(false);

  // Check if current user is orgAdmin for delegated creation
  const isOrgAdmin = userSettings?.orgAccess?.[currentOrganization?.code]?.role === 'orgAdmin' ||
                     userSettings?.orgAccess?.[currentOrganization?.code]?.role === 'superAdmin';

  // Batch update for linked subscriptions
  const [applyToBatch, setApplyToBatch] = useState(false);
  const [batchSubscriptionCount, setBatchSubscriptionCount] = useState(0);

  // Raw input values for email fields (to allow typing commas)
  const [powerBICcInput, setPowerBICcInput] = useState('');
  const [powerBIBccInput, setPowerBIBccInput] = useState('');
  const [nativeCcInput, setNativeCcInput] = useState('');

  // Power BI form data
  const [powerBIFormData, setPowerBIFormData] = useState({
    reportId: '',
    triggerTime: {
      frequency: 'weekly',
      days: [1], // Monday for weekly, day 1 for monthly
      times: ['08:00'],
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    },
    reportConfig: {
      applyFilters: false,
      filterState: null
    },
    deliveryConfig: {
      deliveryMethod: 'email',
      emailConfig: {
        ccRecipients: [],
        bccRecipients: [],
        customSubject: '',
        includeAttachment: true,
        attachmentFormat: 'pdf',
        includeInlineData: false,
        customMessage: ''
      }
    }
  });

  // Native report form data
  const [nativeFormData, setNativeFormData] = useState({
    reportType: 'daily-performance',
    locations: [],
    includePortfolioSummary: false,
    includeLocationDetail: false,
    triggerTime: {
      frequency: 'daily',
      time: '08:00',
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
    },
    deliveryConfig: {
      recipients: [],
      ccRecipients: [],
      customSubject: '',
      includeExcelAttachment: true
    }
  });

  const frequencies = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'monthly', label: 'Monthly' }
  ];

  const weekDays = [
    { value: 0, label: 'Sunday' },
    { value: 1, label: 'Monday' },
    { value: 2, label: 'Tuesday' },
    { value: 3, label: 'Wednesday' },
    { value: 4, label: 'Thursday' },
    { value: 5, label: 'Friday' },
    { value: 6, label: 'Saturday' }
  ];

  const monthDays = Array.from({ length: 31 }, (_, i) => ({
    value: i + 1,
    label: (i + 1).toString()
  }));

  const attachmentFormats = [
    { value: 'pdf', label: 'PDF' },
    { value: 'png', label: 'PNG Image' },
    { value: 'pptx', label: 'PowerPoint (PPTX)' }
  ];

  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Phoenix',
    'America/Anchorage',
    'Pacific/Honolulu',
    'Asia/Tokyo',
    'UTC'
  ];

  const nativeReportTypes = [
    { value: 'daily-performance', label: 'Daily Performance Report' },
    { value: 'daily-sales-labor', label: 'Daily Sales & Labor Report' },
    { value: 'key-stats-export', label: 'Key Stats Export' }
  ];

  // Load existing subscription data for editing
  useEffect(() => {
    if (mode === 'edit' && subscription) {
      // Determine if this is a Power BI or Native subscription
      if (subscription.reportId) {
        // Power BI subscription
        setSelectedReportType(REPORT_TYPE_POWERBI);
        setPowerBIFormData({
          reportId: subscription.reportId || '',
          triggerTime: {
            frequency: subscription.triggerTime?.frequency || 'weekly',
            days: subscription.triggerTime?.days || [1],
            times: subscription.triggerTime?.times || ['08:00'],
            timezone: subscription.triggerTime?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
          },
          reportConfig: {
            applyFilters: subscription.reportConfig?.applyFilters || false,
            filterState: subscription.reportConfig?.filterState || null
          },
          deliveryConfig: {
            deliveryMethod: 'email',
            emailConfig: {
              ccRecipients: subscription.deliveryConfig?.emailConfig?.ccRecipients || [],
              bccRecipients: subscription.deliveryConfig?.emailConfig?.bccRecipients || [],
              customSubject: subscription.deliveryConfig?.emailConfig?.customSubject || '',
              includeAttachment: subscription.deliveryConfig?.emailConfig?.includeAttachment !== false,
              attachmentFormat: subscription.deliveryConfig?.emailConfig?.attachmentFormat || 'pdf',
              includeInlineData: subscription.deliveryConfig?.emailConfig?.includeInlineData || false,
              customMessage: subscription.deliveryConfig?.emailConfig?.customMessage || ''
            }
          }
        });
        // Initialize raw input values for Power BI
        setPowerBICcInput((subscription.deliveryConfig?.emailConfig?.ccRecipients || []).join(', '));
        setPowerBIBccInput((subscription.deliveryConfig?.emailConfig?.bccRecipients || []).join(', '));
      } else {
        // Native subscription
        setSelectedReportType(REPORT_TYPE_NATIVE);
        setNativeFormData({
          reportType: subscription.reportType || 'daily-performance',
          locations: subscription.locations || [],
          includePortfolioSummary: subscription.includePortfolioSummary || false,
          includeLocationDetail: subscription.includeLocationDetail || false,
          triggerTime: {
            frequency: subscription.triggerTime?.frequency || 'daily',
            time: subscription.triggerTime?.time || '08:00',
            timezone: subscription.triggerTime?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone
          },
          deliveryConfig: {
            recipients: subscription.deliveryConfig?.recipients || [],
            ccRecipients: subscription.deliveryConfig?.ccRecipients || [],
            customSubject: subscription.deliveryConfig?.customSubject || '',
            includeExcelAttachment: subscription.deliveryConfig?.includeExcelAttachment !== false
          }
        });
        // Initialize raw input value for Native CC
        setNativeCcInput((subscription.deliveryConfig?.ccRecipients || []).join(', '));

        // For edit mode, set the target user to the subscription's userId
        // This will trigger loading their accessible locations
        if (subscription.userId) {
          setSelectedTargetUsers([subscription.userId]);
        }
      }

      // If this subscription is part of a batch, load the batch info
      if (subscription.batchId && subscription._batchCount) {
        setBatchSubscriptionCount(subscription._batchCount);

        // If this is a batch edit (user clicked edit on the batch group row),
        // pre-select the "apply to batch" checkbox
        if (subscription._batchEdit) {
          setApplyToBatch(true);
        }
      }
    }
  }, [subscription, mode]);

  // Load available Power BI reports
  useEffect(() => {
    const loadReports = async () => {
      try {
        if (!currentOrganization || !userSettings?.orgAccess) return;

        const orgAccess = userSettings.orgAccess[currentOrganization.code];
        if (!orgAccess) return;

        const reportIds = Object.keys(orgAccess.reportIds || {});
        if (reportIds.length === 0) return;

        let workspaceId = currentOrganization.workspaceId;
        if (!workspaceId) {
          const orgConfig = await getOrgConfig();
          const selectedOrgConfig = orgConfig.find(config => config.orgCode === currentOrganization.code);
          if (!selectedOrgConfig) return;
          workspaceId = selectedOrgConfig.workspaceId;
        }

        const reportsResponse = await getReports(workspaceId);
        const filteredReports = reportsResponse.value.filter(report =>
          reportIds.includes(report.id)
        );

        setAvailableReports(filteredReports);
      } catch (error) {
        console.error('Error loading reports:', error);
      }
    };

    loadReports();
  }, [userSettings, currentOrganization]);

  // Load available locations for native reports
  useEffect(() => {
    const loadLocations = async () => {
      try {
        if (!currentOrganization) return;
        const locations = await getLocationsByOrgId(currentOrganization.id);
        setAvailableLocations(locations || []);
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };

    loadLocations();
  }, [currentOrganization]);

  // Load users in organization for delegated subscription creation (orgAdmins only)
  useEffect(() => {
    const loadUsers = async () => {
      try {
        if (!currentOrganization || !isOrgAdmin) {
          console.log('Skipping user load - orgAdmin check:', isOrgAdmin, 'org:', currentOrganization?.id);
          return;
        }

        console.log('Loading users for org:', currentOrganization.code);
        const users = await getUsersInOrganization(currentOrganization.code);
        console.log('Loaded users:', users?.length, users);
        setAvailableUsers(users || []);
      } catch (error) {
        console.error('Error loading users:', error);
      }
    };

    loadUsers();
  }, [currentOrganization, isOrgAdmin]);

  // When target users change, fetch their accessible locations (for native reports)
  useEffect(() => {
    const loadTargetUsersLocations = async () => {
      if (!selectedTargetUsers || selectedTargetUsers.length === 0 || !currentOrganization || selectedReportType !== REPORT_TYPE_NATIVE) {
        console.log('Skipping location load:', { selectedTargetUsers, currentOrganization, selectedReportType });
        return;
      }

      console.log('Loading locations for users:', selectedTargetUsers);
      console.log('Current user email:', userSettings?.email);

      // If only creating for self, use the default locations
      if (selectedTargetUsers.length === 1 && selectedTargetUsers[0] === userSettings?.email) {
        console.log('Loading locations for SELF only - showing all org locations');
        setTargetUsersInfo({});
        // Reload all locations for the org
        try {
          const locations = await getLocationsByOrgId(currentOrganization.id);
          setAvailableLocations(locations || []);
        } catch (error) {
          console.error('Error reloading locations:', error);
        }
        return;
      }

      console.log('Loading locations for OTHER users - will filter by permissions');

      try {
        setLoadingUserLocations(true);
        const usersInfo = {};
        const allUserLocationSets = [];

        // Fetch location access for each selected user
        for (const userEmail of selectedTargetUsers) {
          if (userEmail === userSettings?.email) {
            // For self, get all org locations
            const locations = await getLocationsByOrgId(currentOrganization.id);
            usersInfo[userEmail] = {
              userId: userEmail,
              role: 'self',
              isOrgAdmin: isOrgAdmin,
              locations: locations,
              allLocationsAccessible: isOrgAdmin
            };
            allUserLocationSets.push(locations.map(loc => loc.id));
          } else {
            const userAccessInfo = await getUserAccessibleLocations(userEmail, currentOrganization.code);
            usersInfo[userEmail] = userAccessInfo;
            allUserLocationSets.push(userAccessInfo.locations.map(loc => loc.id));
          }
        }

        setTargetUsersInfo(usersInfo);

        console.log('All user location sets:', allUserLocationSets);

        // Calculate intersection of all users' accessible locations
        // Only show locations that ALL selected users have access to
        const commonLocationIds = allUserLocationSets.reduce((acc, locationIds) => {
          return acc.filter(id => locationIds.includes(id));
        });

        console.log('Common location IDs after intersection:', commonLocationIds);

        // Get the full location objects for common locations
        const allLocations = Object.values(usersInfo).flatMap(info => info.locations);
        const uniqueLocations = Array.from(
          new Map(allLocations.map(loc => [loc.id, loc])).values()
        ).filter(loc => commonLocationIds.includes(loc.id));

        console.log('Unique filtered locations:', uniqueLocations);
        setAvailableLocations(uniqueLocations);

        // Clear any selected locations that not all users have access to
        const filteredLocations = nativeFormData.locations.filter(locId =>
          commonLocationIds.includes(locId)
        );

        if (filteredLocations.length !== nativeFormData.locations.length) {
          handleNativeInputChange('locations', filteredLocations);
        }
      } catch (error) {
        console.error('Error loading target users locations:', error);
        setError(`Failed to load locations for selected users`);
      } finally {
        setLoadingUserLocations(false);
      }
    };

    loadTargetUsersLocations();
  }, [selectedTargetUsers, currentOrganization, selectedReportType]);

  // Auto-populate recipients field with selected target users
  useEffect(() => {
    if (mode === 'create' && selectedReportType === REPORT_TYPE_NATIVE) {
      // Set recipients to selected target users
      handleNativeInputChange('deliveryConfig.recipients', selectedTargetUsers.filter(email => email));
    }
  }, [selectedTargetUsers, selectedReportType, mode]);

  const handlePowerBIInputChange = (path, value) => {
    setPowerBIFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleNativeInputChange = (path, value) => {
    setNativeFormData(prev => {
      const newData = { ...prev };
      const keys = path.split('.');
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }

      current[keys[keys.length - 1]] = value;
      return newData;
    });
  };

  const handleDaysChange = (dayValue) => {
    const currentDays = [...powerBIFormData.triggerTime.days];
    const index = currentDays.indexOf(dayValue);

    if (index > -1) {
      currentDays.splice(index, 1);
    } else {
      currentDays.push(dayValue);
    }

    if (currentDays.length > 0) {
      handlePowerBIInputChange('triggerTime.days', currentDays.sort((a, b) => a - b));
    }
  };

  const handleTimeChange = (index, value) => {
    const newTimes = [...powerBIFormData.triggerTime.times];
    newTimes[index] = value;
    handlePowerBIInputChange('triggerTime.times', newTimes);
  };

  const addTime = () => {
    const newTimes = [...powerBIFormData.triggerTime.times, '12:00'];
    handlePowerBIInputChange('triggerTime.times', newTimes);
  };

  const removeTime = (index) => {
    if (powerBIFormData.triggerTime.times.length > 1) {
      const newTimes = powerBIFormData.triggerTime.times.filter((_, i) => i !== index);
      handlePowerBIInputChange('triggerTime.times', newTimes);
    }
  };

  const handleLocationToggle = (locationId) => {
    const currentLocations = [...nativeFormData.locations];
    const index = currentLocations.indexOf(locationId);

    if (index > -1) {
      currentLocations.splice(index, 1);
    } else {
      currentLocations.push(locationId);
    }

    handleNativeInputChange('locations', currentLocations.sort((a, b) => a - b));
  };

  const handleSelectAllLocations = () => {
    const allLocationIds = availableLocations.map(loc => loc.id);
    handleNativeInputChange('locations', allLocationIds);
  };

  const handleDeselectAllLocations = () => {
    handleNativeInputChange('locations', []);
  };

  const handleEmailListChange = (path, value, isNative = false) => {
    const emails = value.split(',').map(email => email.trim()).filter(email => email);
    if (isNative) {
      handleNativeInputChange(path, emails);
    } else {
      handlePowerBIInputChange(path, emails);
    }
  };

  // Parse email list from raw input on blur
  const parseEmailsOnBlur = (rawValue, path, isNative, setRawInput) => {
    const emails = rawValue.split(',').map(email => email.trim()).filter(email => email);
    if (isNative) {
      handleNativeInputChange(path, emails);
    } else {
      handlePowerBIInputChange(path, emails);
    }
    // Update raw input to cleaned format (removes trailing commas, extra spaces)
    setRawInput(emails.join(', '));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    // Parse raw email inputs for submission (in case user submits without blurring)
    const parsedNativeCc = nativeCcInput.split(',').map(email => email.trim()).filter(email => email);
    const parsedPowerBICc = powerBICcInput.split(',').map(email => email.trim()).filter(email => email);
    const parsedPowerBIBcc = powerBIBccInput.split(',').map(email => email.trim()).filter(email => email);

    try {
      let result;

      if (selectedReportType === REPORT_TYPE_NATIVE) {
        // Native report submission
        if (!currentOrganization?.id) {
          setError('Missing organization ID. Please contact your administrator.');
          setLoading(false);
          return;
        }

        // If creating for multiple users, create a subscription for each
        if (mode === 'create' && selectedTargetUsers.length > 0) {
          const results = [];
          // Generate a batchId if creating for multiple users
          const batchId = selectedTargetUsers.length > 1 ? `batch-${Date.now()}-${Math.random().toString(36).substr(2, 9)}` : null;

          for (const targetUserEmail of selectedTargetUsers) {
            const submissionData = {
              ...nativeFormData,
              deliveryConfig: {
                ...nativeFormData.deliveryConfig,
                ccRecipients: parsedNativeCc
              },
              userId: targetUserEmail,
              orgId: currentOrganization.id,
              batchId: batchId // Link all subscriptions created together
            };

            const validation = validateNativeReportSubscription(submissionData);
            if (!validation.isValid) {
              setError(`Validation failed for ${targetUserEmail}: ${validation.errors.join(', ')}`);
              setLoading(false);
              return;
            }

            const created = await createNativeReportSubscription(submissionData);
            results.push(created);
          }
          result = results[0]; // Return first one for the callback
          if (batchId) {
            result.batchId = batchId; // Include batchId in result for UI reference
          }
        } else if (mode === 'edit') {
          // When editing, keep the original userId
          const submissionData = {
            ...nativeFormData,
            deliveryConfig: {
              ...nativeFormData.deliveryConfig,
              ccRecipients: parsedNativeCc
            },
            userId: subscription.userId,
            orgId: currentOrganization.id,
            applyToBatch: applyToBatch // Include batch update flag if checked
          };

          const validation = validateNativeReportSubscription(submissionData);
          if (!validation.isValid) {
            setError(`Validation failed: ${validation.errors.join(', ')}`);
            setLoading(false);
            return;
          }

          result = await updateNativeReportSubscription(subscription.id, submissionData);
        }
      } else {
        // Power BI report submission
        const workspaceId = currentOrganization?.workspaceId;

        if (!workspaceId) {
          setError('Missing workspace ID for current organization. Please contact your administrator.');
          setLoading(false);
          return;
        }

        // Stamp the report's display name on the doc so the subscription
        // runner can scope the PDF to this report's pages without a PBI
        // workspace lookup (and after the workspace is retired).
        const chosenReportName = availableReports.find(r => r.id === powerBIFormData.reportId)?.name
          || subscription?.reportName
          || undefined;

        // If creating for multiple users, create a subscription for each
        if (mode === 'create' && selectedTargetUsers.length > 0) {
          const results = [];
          for (const targetUserEmail of selectedTargetUsers) {
            const submissionData = {
              ...powerBIFormData,
              ...(chosenReportName ? { reportName: chosenReportName } : {}),
              deliveryConfig: {
                ...powerBIFormData.deliveryConfig,
                emailConfig: {
                  ...powerBIFormData.deliveryConfig.emailConfig,
                  ccRecipients: parsedPowerBICc,
                  bccRecipients: parsedPowerBIBcc
                }
              },
              userId: targetUserEmail,
              orgId: currentOrganization?.id,
              workspaceId: workspaceId
            };

            const validation = validateReportSubscription(submissionData);
            if (!validation.isValid) {
              setError(`Validation failed for ${targetUserEmail}: ${validation.errors.join(', ')}`);
              setLoading(false);
              return;
            }

            const created = await createReportSubscription(submissionData);
            results.push(created);
          }
          result = results[0]; // Return first one for the callback
        } else if (mode === 'edit') {
          // When editing, keep the original userId
          const submissionData = {
            ...powerBIFormData,
            ...(chosenReportName ? { reportName: chosenReportName } : {}),
            deliveryConfig: {
              ...powerBIFormData.deliveryConfig,
              emailConfig: {
                ...powerBIFormData.deliveryConfig.emailConfig,
                ccRecipients: parsedPowerBICc,
                bccRecipients: parsedPowerBIBcc
              }
            },
            userId: subscription.userId,
            orgId: currentOrganization?.id,
            workspaceId: workspaceId
          };

          const validation = validateReportSubscription(submissionData);
          if (!validation.isValid) {
            setError(`Validation failed: ${validation.errors.join(', ')}`);
            setLoading(false);
            return;
          }

          result = await updateReportSubscription(subscription.id, submissionData);
        }
      }

      // Pass additional info about number of users if multiple were selected
      if (mode === 'create' && selectedTargetUsers.length > 1) {
        onSave({
          ...result,
          _multiUser: true,
          _userCount: selectedTargetUsers.length
        });
      } else {
        onSave(result);
      }
    } catch (error) {
      console.error('Error saving subscription:', error);
      setError(error.message || 'Failed to save subscription');
    } finally {
      setLoading(false);
    }
  };

  const selectedReport = availableReports.find(r => r.id === powerBIFormData.reportId);

  const renderPowerBIFields = () => (
    <>
      {/* Report Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Filter className="mr-2" size={20} />
          Report Selection
        </h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Choose Report *
          </label>
          <select
            value={powerBIFormData.reportId}
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'native-daily-performance') {
                setSelectedReportType(REPORT_TYPE_NATIVE);
                handleNativeInputChange('reportType', 'daily-performance');
              } else if (value === 'native-daily-sales-labor') {
                setSelectedReportType(REPORT_TYPE_NATIVE);
                handleNativeInputChange('reportType', 'daily-sales-labor');
              } else if (value === 'native-key-stats-export') {
                setSelectedReportType(REPORT_TYPE_NATIVE);
                handleNativeInputChange('reportType', 'key-stats-export');
              } else {
                handlePowerBIInputChange('reportId', value);
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          >
            <option value="">Select a report...</option>
            {availableReports.map(report => (
              <option key={report.id} value={report.id}>
                {report.name}
              </option>
            ))}
            <option value="native-daily-performance">Daily Summary Report</option>
            <option value="native-daily-sales-labor">Daily Sales & Labor Report</option>
            <option value="native-key-stats-export">Key Stats Export</option>
          </select>
        </div>

        {selectedReport && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-blue-800">
              <strong>Selected:</strong> {selectedReport.name}
            </p>
          </div>
        )}
      </div>

      {/* Schedule Configuration */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Clock className="mr-2" size={20} />
          Schedule Configuration
        </h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Frequency *
          </label>
          <select
            value={powerBIFormData.triggerTime.frequency}
            onChange={(e) => {
              handlePowerBIInputChange('triggerTime.frequency', e.target.value);
              if (e.target.value === 'weekly') {
                handlePowerBIInputChange('triggerTime.days', [1]);
              } else if (e.target.value === 'monthly') {
                handlePowerBIInputChange('triggerTime.days', [1]);
              } else {
                handlePowerBIInputChange('triggerTime.days', [1, 2, 3, 4, 5]);
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {frequencies.map(freq => (
              <option key={freq.value} value={freq.value}>
                {freq.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {powerBIFormData.triggerTime.frequency === 'monthly' ? 'Days of Month *' : 'Days of Week *'}
          </label>
          <div className="grid grid-cols-7 gap-2">
            {(powerBIFormData.triggerTime.frequency === 'monthly' ? monthDays : weekDays).map(day => (
              <label key={day.value} className="flex items-center">
                <input
                  type="checkbox"
                  checked={powerBIFormData.triggerTime.days.includes(day.value)}
                  onChange={() => handleDaysChange(day.value)}
                  className="mr-2"
                />
                <span className="text-sm">{day.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Times *
          </label>
          <div className="space-y-2">
            {powerBIFormData.triggerTime.times.map((time, index) => (
              <div key={index} className="flex items-center gap-2">
                <input
                  type="time"
                  value={time}
                  onChange={(e) => handleTimeChange(index, e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                {powerBIFormData.triggerTime.times.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeTime(index)}
                    className="px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              onClick={addTime}
              className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg border border-blue-300"
            >
              Add Time
            </button>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timezone *
          </label>
          <select
            value={powerBIFormData.triggerTime.timezone}
            onChange={(e) => handlePowerBIInputChange('triggerTime.timezone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {timezones.map(tz => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Email Configuration */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Mail className="mr-2" size={20} />
          Email Configuration
        </h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subject *
          </label>
          <input
            type="text"
            value={powerBIFormData.deliveryConfig.emailConfig.customSubject}
            onChange={(e) => handlePowerBIInputChange('deliveryConfig.emailConfig.customSubject', e.target.value)}
            placeholder="Daily Revenue Report"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CC Recipients (comma-separated emails)
          </label>
          <input
            type="text"
            value={powerBICcInput}
            onChange={(e) => setPowerBICcInput(e.target.value)}
            onBlur={() => parseEmailsOnBlur(powerBICcInput, 'deliveryConfig.emailConfig.ccRecipients', false, setPowerBICcInput)}
            placeholder="user1@example.com, user2@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            BCC Recipients (comma-separated emails)
          </label>
          <input
            type="text"
            value={powerBIBccInput}
            onChange={(e) => setPowerBIBccInput(e.target.value)}
            onBlur={() => parseEmailsOnBlur(powerBIBccInput, 'deliveryConfig.emailConfig.bccRecipients', false, setPowerBIBccInput)}
            placeholder="admin@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Attachment Format
          </label>
          <select
            value={powerBIFormData.deliveryConfig.emailConfig.attachmentFormat}
            onChange={(e) => handlePowerBIInputChange('deliveryConfig.emailConfig.attachmentFormat', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {attachmentFormats.map(format => (
              <option key={format.value} value={format.value}>
                {format.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={powerBIFormData.deliveryConfig.emailConfig.includeAttachment}
              onChange={(e) => handlePowerBIInputChange('deliveryConfig.emailConfig.includeAttachment', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700">Include attachment</span>
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom Message (optional)
          </label>
          <textarea
            value={powerBIFormData.deliveryConfig.emailConfig.customMessage}
            onChange={(e) => handlePowerBIInputChange('deliveryConfig.emailConfig.customMessage', e.target.value)}
            placeholder="Add a custom message to include in the email..."
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
      </div>

      {/* Filter Configuration */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Filter className="mr-2" size={20} />
          Filter Configuration
        </h3>

        <div>
          <label className="flex items-center">
            <input
              type="checkbox"
              checked={powerBIFormData.reportConfig.applyFilters}
              onChange={(e) => handlePowerBIInputChange('reportConfig.applyFilters', e.target.checked)}
              className="mr-2"
            />
            <span className="text-sm font-medium text-gray-700">Apply saved filters</span>
          </label>
          <p className="text-sm text-gray-500 mt-1">
            When enabled, the report will be generated with any filters you have saved for this report.
          </p>
        </div>
      </div>
    </>
  );

  const renderNativeFields = () => (
    <>
      {/* Report Type Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Database className="mr-2" size={20} />
          Report Type
        </h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Choose Report Type *
          </label>
          <select
            value={nativeFormData.reportType}
            onChange={(e) => {
              if (e.target.value === 'powerbi-switch') {
                setSelectedReportType(REPORT_TYPE_POWERBI);
              } else {
                handleNativeInputChange('reportType', e.target.value);
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            required
          >
            <option value="daily-performance">Daily Summary Report</option>
            <option value="daily-sales-labor">Daily Sales & Labor Report</option>
            <option value="key-stats-export">Key Stats Export</option>
            <option value="powerbi-switch">← Switch to Other Reports</option>
          </select>
          <p className="text-sm text-gray-600 mt-2">
            {nativeFormData.reportType === 'daily-sales-labor'
              ? 'Daily sales metrics combined with labor data including hours worked, overtime, labor % of revenue, and hours per occupied room by department.'
              : nativeFormData.reportType === 'key-stats-export'
              ? 'Excel export with End of Day (prior day) and Period to Date tabs: one row per hotel with occupancy, ADR, RevPAR, market segment room revenue and nights, and revenue by category. Optionally includes per-location detail tabs.'
              : 'Daily performance metrics including occupancy, ADR, RevPAR, room revenue, F&B revenue, and other revenue. Supports multiple locations with portfolio summary.'
            }
          </p>
        </div>
      </div>

      {/* User Selection (for orgAdmins creating on behalf of others) */}
      {isOrgAdmin && mode === 'create' && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <User className="mr-2" size={20} />
            Create Subscription For
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Users * (can select multiple)
            </label>
            <div className="border border-gray-300 rounded-lg max-h-60 overflow-y-auto p-3 space-y-2">
              {/* Myself option */}
              <label className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedTargetUsers.includes(userSettings?.email)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedTargetUsers([...selectedTargetUsers, userSettings?.email]);
                    } else {
                      setSelectedTargetUsers(selectedTargetUsers.filter(u => u !== userSettings?.email));
                    }
                  }}
                  className="mr-3 h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-900">Myself ({userSettings?.email})</span>
              </label>

              {/* Other users */}
              {availableUsers.length > 0 ? (
                availableUsers
                  .filter(u => u.email !== userSettings?.email && !u.isDeleted)
                  .map(user => (
                    <label key={user.email} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedTargetUsers.includes(user.email)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTargetUsers([...selectedTargetUsers, user.email]);
                          } else {
                            setSelectedTargetUsers(selectedTargetUsers.filter(u => u !== user.email));
                          }
                        }}
                        className="mr-3 h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                      />
                      <span className="text-sm text-gray-900">
                        {user.firstName && user.lastName
                          ? `${user.firstName} ${user.lastName} (${user.email})`
                          : user.email}
                      </span>
                    </label>
                  ))
              ) : (
                <p className="text-sm text-gray-500 p-2">Loading users...</p>
              )}
            </div>

            <div className="mt-2 text-sm text-gray-600">
              {selectedTargetUsers.length} user{selectedTargetUsers.length !== 1 ? 's' : ''} selected
              {selectedTargetUsers.length > 1 && ' - A subscription will be created for each user'}
            </div>

            {selectedTargetUsers.some(email => email !== userSettings?.email) && (
              <div className="mt-2 bg-blue-50 border border-blue-200 rounded-lg p-3">
                <div className="flex items-start">
                  <Info size={16} className="text-blue-600 mr-2 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <strong>Delegated Creation:</strong> Creating subscriptions for {selectedTargetUsers.length} user{selectedTargetUsers.length !== 1 ? 's' : ''}.
                    {selectedTargetUsers.length > 1 && (
                      <div className="mt-1">
                        Only locations accessible to <strong>all</strong> selected users will be available.
                      </div>
                    )}
                    {Object.keys(targetUsersInfo).length > 0 && (
                      <div className="mt-2 space-y-1">
                        {Object.entries(targetUsersInfo).map(([email, info]) => (
                          <div key={email} className="text-xs">
                            • {email}: {info.role}
                            {info.isOrgAdmin && ' (All locations)'}
                            {!info.isOrgAdmin && ` (${info.locations?.length || 0} locations)`}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Location Selection */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Building2 className="mr-2" size={20} />
          Location Selection
          {loadingUserLocations && (
            <span className="ml-2 text-sm text-gray-500 flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-500 border-t-transparent mr-2"></div>
              Loading user locations...
            </span>
          )}
        </h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Locations * (at least one required)
          </label>
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={handleSelectAllLocations}
              className="px-3 py-1 text-sm bg-orange-50 text-orange-700 border border-orange-300 rounded hover:bg-orange-100"
            >
              Select All
            </button>
            <button
              type="button"
              onClick={handleDeselectAllLocations}
              className="px-3 py-1 text-sm bg-gray-50 text-gray-700 border border-gray-300 rounded hover:bg-gray-100"
            >
              Deselect All
            </button>
            <span className="text-sm text-gray-600 ml-auto self-center">
              {nativeFormData.locations.length} location{nativeFormData.locations.length !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="border border-gray-300 rounded-lg max-h-60 overflow-y-auto p-3 space-y-2">
            {availableLocations.length === 0 ? (
              <p className="text-sm text-gray-500">No locations available for this organization</p>
            ) : (
              availableLocations.map(location => (
                <label key={location.id} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                  <input
                    type="checkbox"
                    checked={nativeFormData.locations.includes(location.id)}
                    onChange={() => handleLocationToggle(location.id)}
                    className="mr-3 h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
                  />
                  <span className="text-sm text-gray-900">{location.name || `Location ${location.id}`}</span>
                </label>
              ))
            )}
          </div>
        </div>

        {nativeFormData.reportType === 'key-stats-export' ? (
          <div>
            <label className="flex items-center p-3 bg-orange-50 border border-orange-200 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors">
              <input
                type="checkbox"
                checked={nativeFormData.includeLocationDetail}
                onChange={(e) => handleNativeInputChange('includeLocationDetail', e.target.checked)}
                className="mr-3 h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Include Location Detail Tabs</span>
                <p className="text-xs text-gray-600 mt-1">
                  Add a detail tab per location after the EOD and PTD summary tabs. Leave unchecked for the portfolio summary only.
                </p>
              </div>
            </label>
          </div>
        ) : nativeFormData.locations.length > 1 && (
          <div>
            <label className="flex items-center p-3 bg-orange-50 border border-orange-200 rounded-lg cursor-pointer hover:bg-orange-100 transition-colors">
              <input
                type="checkbox"
                checked={nativeFormData.includePortfolioSummary}
                onChange={(e) => handleNativeInputChange('includePortfolioSummary', e.target.checked)}
                className="mr-3 h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Include Portfolio Summary</span>
                <p className="text-xs text-gray-600 mt-1">
                  Add a summary sheet/section with aggregated metrics across all selected locations
                </p>
              </div>
            </label>
          </div>
        )}
      </div>

      {/* Schedule Configuration */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Clock className="mr-2" size={20} />
          Schedule Configuration
        </h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Frequency *
          </label>
          <select
            value={nativeFormData.triggerTime.frequency}
            onChange={(e) => handleNativeInputChange('triggerTime.frequency', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            <option value="daily">Daily</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Currently only daily frequency is supported. Weekly and monthly coming soon.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Delivery Time *
          </label>
          <input
            type="time"
            value={nativeFormData.triggerTime.time}
            onChange={(e) => handleNativeInputChange('triggerTime.time', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            Report will be sent daily at this time (for yesterday's data)
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Timezone *
          </label>
          <select
            value={nativeFormData.triggerTime.timezone}
            onChange={(e) => handleNativeInputChange('triggerTime.timezone', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          >
            {timezones.map(tz => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Email Configuration */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <Mail className="mr-2" size={20} />
          Email Configuration
        </h3>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Subject *
          </label>
          <input
            type="text"
            value={nativeFormData.deliveryConfig.customSubject}
            onChange={(e) => handleNativeInputChange('deliveryConfig.customSubject', e.target.value)}
            placeholder="Daily Performance Report"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Primary Recipients * {mode === 'create' && '(auto-populated from selected users)'}
            {mode === 'edit' && subscription?.metadata?.isDelegated && ' (read-only for delegated subscriptions)'}
          </label>
          <input
            type="text"
            value={nativeFormData.deliveryConfig.recipients.join(', ')}
            readOnly={mode === 'create' || (mode === 'edit' && subscription?.metadata?.isDelegated)}
            disabled={mode === 'create' || (mode === 'edit' && subscription?.metadata?.isDelegated)}
            onChange={(e) => mode === 'edit' && !subscription?.metadata?.isDelegated && handleEmailListChange('deliveryConfig.recipients', e.target.value, true)}
            placeholder="Recipients will be auto-populated"
            className={`w-full px-3 py-2 border rounded-lg ${
              (mode === 'create' || (mode === 'edit' && subscription?.metadata?.isDelegated))
                ? 'bg-gray-50 border-gray-300 text-gray-700 cursor-not-allowed'
                : 'border-gray-300 focus:outline-none focus:ring-2 focus:ring-orange-500'
            }`}
            required
          />
          <p className="text-xs text-gray-500 mt-1">
            {mode === 'create'
              ? 'Recipients are automatically set to the selected users above'
              : subscription?.metadata?.isDelegated
              ? 'Recipients cannot be changed for delegated subscriptions'
              : 'At least one recipient is required'}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            CC Recipients (comma-separated emails)
          </label>
          <input
            type="text"
            value={nativeCcInput}
            onChange={(e) => setNativeCcInput(e.target.value)}
            onBlur={() => parseEmailsOnBlur(nativeCcInput, 'deliveryConfig.ccRecipients', true, setNativeCcInput)}
            placeholder="manager@example.com"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
          />
        </div>

        <div>
          <label className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg cursor-pointer hover:bg-green-100 transition-colors">
            <input
              type="checkbox"
              checked={nativeFormData.deliveryConfig.includeExcelAttachment}
              onChange={(e) => handleNativeInputChange('deliveryConfig.includeExcelAttachment', e.target.checked)}
              className="mr-3 h-4 w-4 text-orange-500 focus:ring-orange-500 border-gray-300 rounded"
            />
            <div>
              <span className="text-sm font-medium text-gray-900">Include Excel Attachment</span>
              <p className="text-xs text-gray-600 mt-1">
                Attach a professionally formatted Excel workbook with all metrics
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Batch Update Option - Only show in edit mode for subscriptions with batchId */}
      {mode === 'edit' && subscription?.batchId && batchSubscriptionCount > 1 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <Globe className="mr-2" size={20} />
            Linked Subscriptions
          </h3>

          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-start mb-3">
              <Info size={16} className="text-purple-600 mr-2 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-purple-800">
                <strong>Subscription Group:</strong> This subscription is linked to {batchSubscriptionCount - 1} other subscription{batchSubscriptionCount - 1 !== 1 ? 's' : ''} that were created at the same time.
              </div>
            </div>

            <label className="flex items-center p-3 bg-white border border-purple-300 rounded-lg cursor-pointer hover:bg-purple-50 transition-colors">
              <input
                type="checkbox"
                checked={applyToBatch}
                onChange={(e) => setApplyToBatch(e.target.checked)}
                className="mr-3 h-4 w-4 text-purple-500 focus:ring-purple-500 border-gray-300 rounded"
              />
              <div>
                <span className="text-sm font-medium text-gray-900">Apply changes to all {batchSubscriptionCount} linked subscriptions</span>
                <p className="text-xs text-gray-600 mt-1">
                  When checked, your changes will be applied to all {batchSubscriptionCount} subscriptions in this group. Each subscription will keep its own recipient list.
                </p>
              </div>
            </label>
          </div>
        </div>
      )}

    </>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900 flex items-center">
            {selectedReportType === REPORT_TYPE_NATIVE && <Database size={28} className="mr-3 text-orange-500" />}
            {mode === 'edit' ? 'Edit Subscription' : 'Create New Subscription'}
          </h2>
          <button
            onClick={onCancel}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={24} className="text-gray-500" />
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mx-6 mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {selectedReportType === REPORT_TYPE_POWERBI ? renderPowerBIFields() : renderNativeFields()}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`px-6 py-2 ${selectedReportType === REPORT_TYPE_NATIVE ? 'bg-orange-500 hover:bg-orange-600' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-lg transition-colors disabled:opacity-50 flex items-center`}
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  {mode === 'edit' ? 'Update Subscription' : 'Create Subscription'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UnifiedSubscriptionForm;
